import {
  CreateButton,
  Datagrid,
  Filter,
  FunctionField,
  TextField,
  TextInput,
  Record,
  useListContext,
  Link,
  Button,
} from "react-admin";
import apiClients from ".";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { DeleteRecordMenuItem } from "../../common/data-grid-fields/DeleteRecordMenuItem";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import { IResourceListProps } from "../../common/ra-types";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import RedirectedApiClientsStatusLabel from "../../master/redirected-api-clients/RedirectedApiClientsStatusLabel";
import {
  ApiClientListWithRedirectionDto,
  PublicApiRedirectionRequestStatus,
} from "../../../common/data/service-proxies";
import { Check, Close } from "@material-ui/icons";
import { useTheme } from "@material-ui/core/styles";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { ViewRedirectedApiClientMenuItem } from "./ViewRedirectedApiClientMenuItem";
import PageviewOutlinedIcon from "@material-ui/icons/PageviewOutlined";
import ContentAdd from "@material-ui/icons/Add";
import resourceNames from "../../../common/data/resourceNames";

interface Props extends IResourceListProps {}

const ListFilter = (props: any) => {
  useSearchFields(props, ["name"]);
  useResetListSearchField(props);
  const { loading } = useListContext();
  return (
    <Filter {...props}>
      {!loading && (
        <TextInput
          className="companies-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      {loading && (
        <TextInput
          className="companies-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
    </Filter>
  );
};

const ListActions = ({
  hasRedirectedRequestsPermission,
}: {
  hasRedirectedRequestsPermission: boolean;
}) => {
  const { basePath } = useListContext();
  return (
    <>
      {hasRedirectedRequestsPermission && (
        <Button
          component={Link}
          to={`/${resourceNames.PUBLIC_API_REDIRECTION_REQUESTS}/create`}
          label="resources.admin-api-clients.labels.createRedirectedApi"
        >
          <ContentAdd />
        </Button>
      )}
      <CreateButton basePath={basePath} />
    </>
  );
};

export const ApiClientsList = (props: Props) => {
  const theme = useTheme();
  const permissions = useActionPermissions();
  const hasRedirectedRequestsPermission = !!(
    permissions &&
    permissions[permissionNames.admin.publicApiRedirectionRequests.request]
  );

  return (
    <div className="content-container">
      <DefaultProLogsList
        empty={<ProLogsEmpty iconType={apiClients.icon} />}
        className="content-list"
        title="resources.admin-api-clients.name"
        bulkActionButtons={false}
        actions={
          <ListActions
            hasRedirectedRequestsPermission={hasRedirectedRequestsPermission}
          />
        }
        filters={<ListFilter />}
        pagination={false}
        {...props}
      >
        <Datagrid>
          <TextField
            source="name"
            label="resources.admin-api-clients.labels.name"
          />
          {hasRedirectedRequestsPermission && (
            <FunctionField<ApiClientListWithRedirectionDto & Record>
              source="hasRedirectionRequest"
              label="resources.admin-api-clients.labels.redirected"
              render={(record) => {
                return record?.hasRedirectionRequest ? (
                  <Check style={{ color: theme.palette.success.main }} />
                ) : (
                  <Close style={{ color: theme.palette.error.main }} />
                );
              }}
            />
          )}

          {hasRedirectedRequestsPermission && (
            <FunctionField<ApiClientListWithRedirectionDto & Record>
              source="redirectionRequestStatus"
              label="resources.admin-api-clients.labels.status"
              render={(record) => {
                if (!record || !record.redirectionRequestStatus) return "-";
                return (
                  <RedirectedApiClientsStatusLabel
                    status={record.redirectionRequestStatus}
                  />
                );
              }}
            />
          )}
          <ActionsField hasShow={props.hasShow}>
            <ViewRedirectedApiClientMenuItem
              hidden={(record) =>
                !hasRedirectedRequestsPermission ||
                !permissions[permissionNames.admin.apiClients.main] ||
                !permissions[permissionNames.admin.apiClients.update] ||
                record.hasRedirectionRequest !== true ||
                record.redirectionRequestStatus !==
                  PublicApiRedirectionRequestStatus.Approved
              }
              label="resources.admin-api-clients.labels.view"
              icon={<PageviewOutlinedIcon />}
            />
            <DeleteRecordMenuItem
              hidden={(record) =>
                record.redirectionRequestStatus ===
                PublicApiRedirectionRequestStatus.Pending
              }
            />
          </ActionsField>
        </Datagrid>
      </DefaultProLogsList>
    </div>
  );
};
